import { ActionIcon, Badge, Box, Group, Modal, Paper, Text, Tooltip } from "@mantine/core";
import { IMCBIMAPI } from "@mctypes/mcbim.types";
import { IconAlertTriangle, IconInfoCircle } from "@tabler/icons-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState } from "react";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { STANDARD_COLORS } from "../../../../globalConstants";
import { formatMillisecondsToString } from "../../../../utils";
import { formatDateTimeCompact } from "../../selectors";
import McBimSingleJob from "./McBimSingleJob";

export const getMcBimFileJobStatusColor = (status: IMCBIMAPI.JobStatus): string => {
  switch (status) {
    case "failed":
      return STANDARD_COLORS.red;
    case "in-progress":
      return STANDARD_COLORS.blue;
    case "queued":
      return STANDARD_COLORS.yellow;
    case "completed":
      return STANDARD_COLORS.green;

    default:
      return STANDARD_COLORS.red;
  }
};

const McJobTable: React.FC<{
  jobs: IMCBIMAPI.IJob[];
  projectId: string;
}> = ({ jobs, projectId }) => {
  const [selectedJob, setSelectedJob] = useState<IMCBIMAPI.IJob>(null);

  const columnDefs = useMemo<ColDef<IMCBIMAPI.IJob>[]>(
    () => [
      {
        headerName: "",
        cellRenderer: ({ data }: ICellRendererParams<IMCBIMAPI.IJob>) => {
          if (data == null) return null;

          return (
            <Tooltip label="Vis detaljer" withArrow>
              <ActionIcon variant="subtle" color="blue" onClick={() => setSelectedJob(data)}>
                <IconInfoCircle size={18} />
              </ActionIcon>
            </Tooltip>
          );
        },
        width: 72,
        sortable: false,
        filter: false,
      },
      {
        field: "createdAt",
        headerName: "Opprettet",
        valueFormatter: ({ value }) => (value != null ? formatDateTimeCompact(value) : ""),
        filter: true,
        flex: 1,
        minWidth: 170,
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        cellRenderer: ({ value }: ICellRendererParams<IMCBIMAPI.IJob, IMCBIMAPI.JobStatus>) => (
          <Badge color={getMantineStatusColor(value)} variant="light">
            {value?.toUpperCase() ?? ""}
          </Badge>
        ),
        filter: true,
        flex: 1,
        minWidth: 140,
        sortable: true,
      },
      {
        field: "duration",
        headerName: "Tid brukt [min:sek:ms]",
        valueFormatter: ({ value }) => formatMillisecondsToString(value),
        filter: "agNumberColumnFilter",
        flex: 1,
        minWidth: 190,
        sortable: true,
      },
      {
        field: "completed",
        headerName: "Antall fullførte filer",
        filter: "agNumberColumnFilter",
        flex: 1,
        minWidth: 180,
        sortable: true,
      },
      {
        field: "failed",
        headerName: "Antall mislykkede filer",
        cellRenderer: ({ value }: ICellRendererParams<IMCBIMAPI.IJob, number>) => (
          <Group gap={6}>
            {(value ?? 0) > 0 && <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" />}
            <Text>{value ?? 0}</Text>
          </Group>
        ),
        filter: "agNumberColumnFilter",
        flex: 1,
        minWidth: 190,
        sortable: true,
      },
    ],
    [],
  );

  return (
    <>
      <Paper withBorder radius="md" mt="sm" style={{ height: "calc(100% - 3rem)", overflow: "hidden" }}>
        <Box className="ag-theme-alpine" h="100%">
          <AgGridReact<IMCBIMAPI.IJob>
            rowData={jobs ?? []}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true }}
            getRowId={({ data }) => data.jobId}
            animateRows
            localeText={AG_GRID_LOCALE_NOR}
          />
        </Box>
      </Paper>

      <Modal
        opened={selectedJob != null}
        onClose={() => setSelectedJob(null)}
        title={`Synkronisering ${formatDateTimeCompact(selectedJob?.createdAt)}`}
        size="80vw"
      >
        {selectedJob != null && <McBimSingleJob projectId={projectId} jobId={selectedJob.jobId} />}
      </Modal>
    </>
  );
};

const getMantineStatusColor = (status: IMCBIMAPI.JobStatus) => {
  switch (status) {
    case "failed":
      return "red";
    case "in-progress":
      return "blue";
    case "queued":
      return "yellow";
    case "completed":
      return "green";
    default:
      return "gray";
  }
};

export default McJobTable;
